import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import type {IModalsStore, IModalPayload} from "data/stores/modals/modals.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import {AxiosError} from "axios";
import type {IApiResponse} from "data/services/http";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import {extractErrorMessage} from "data/utils";
import {IPrediction, ISavePredictionParams} from "data/providers/api/predictions.api.provider";
import type {IUserStore} from "data/stores/user/user.store";
import {getToken} from "data/utils/recaptcha";

export interface IModalSaveLocalController extends ViewController {
	i18n: ILocalizationStore;

	get isOpen(): boolean;

	get modalContent(): IModalPayload | null;

	get playerFullName(): string | undefined;

	close: () => void;
	onSave: () => void;
}

@injectable()
export class ModalSaveLocalController implements IModalSaveLocalController {
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.PredictionsStore) public _predictionsStore: IPredictionsStore,
		@inject(Bindings.UserStore) public _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.SAVE_LOCAL_PREDICTION;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get playerFullName() {
		const playerId = this._modalsStore.modalContent?.playerId ?? 0;
		if (!playerId) return;

		const player = this._playerStore.getPlayerById(playerId);
		return `${String(player?.firstName)} ${String(player?.lastName)}`;
	}

	close = () => {
		this._modalsStore.hideModal();
	};

	onSave = () => {
		const localPrediction = JSON.parse(
			localStorage.getItem("localPrediction") || "{}"
		) as IPrediction;
		if (localPrediction) {
			getToken()
				.then((token) => {
					const params: ISavePredictionParams = {
						...localPrediction,
						token,
					};

					if (!this._userStore.isAuthorized) {
						params.guestUid = this._userStore.guestUid;
					}

					return this._predictionsStore.savePrediction(params);
				})
				.then(this.showConfirmModal)
				.catch((err: AxiosError<IApiResponse>) => this.onError(err));
		}
	};

	private showConfirmModal = () => {
		const localPrediction = JSON.parse(
			localStorage.getItem("localPrediction") || "{}"
		) as IPrediction;

		if (localPrediction) {
			this._modalsStore.showModal(ModalType.CONFIRM, {
				message: "",
				playerId: localPrediction.playerId,
				contestId: localPrediction.contestId,
			});
		}
	};

	private onError = (error: AxiosError<IApiResponse>) => {
		const errorMsg = extractErrorMessage(error);

		this._modalsStore.showModal(ModalType.ERROR, {
			message: errorMsg,
		});
	};
}
